import { createContext, useContext, useEffect, useMemo, useReducer, useState, type ReactNode } from "react";
import { gaPricer, track } from "../lib/analytics";
import { useMoneyOptional } from "../lib/CurrencyProvider";
import type { Product, ProductVariant } from "../../shared/types";
import { EMPTY_CART, cartCount, cartReducer, cartSubtotal, deserializeCart, type CartState } from "./cartReducer";

// v2: prices became USD cents; carts saved with peso prices are dropped instead of misread.
export const CART_STORAGE_KEY = "pokefans.cart.v2";

export interface CartValue {
  state: CartState;
  count: number;
  subtotal: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  add: (product: Product, variant: ProductVariant, quantity?: number) => void;
  setQuantity: (variantId: number, quantity: number) => void;
  remove: (variantId: number) => void;
  clear: () => void;
  /** Replaces the whole cart (merging with the server cart on sign-in). */
  replace: (state: CartState) => void;
}

const CartContext = createContext<CartValue | null>(null);

function loadCart(): CartState {
  try {
    return deserializeCart(window.localStorage.getItem(CART_STORAGE_KEY));
  } catch {
    return EMPTY_CART;
  }
}

export function CartProvider({ children, initialState }: { children: ReactNode; initialState?: CartState }) {
  const [state, dispatch] = useReducer(cartReducer, initialState, (init) => init ?? loadCart());
  const [isOpen, setIsOpen] = useState(false);
  const money = useMoneyOptional();

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore quota / privacy-mode failures: the cart still works in memory.
    }
  }, [state]);

  // Keep multiple tabs in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === CART_STORAGE_KEY) dispatch({ type: "replace", state: deserializeCart(e.newValue) });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<CartValue>(
    () => ({
      state,
      count: cartCount(state),
      subtotal: cartSubtotal(state),
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      add: (product, variant, quantity) => {
        dispatch({ type: "add", product, variant, quantity });
        const q = quantity ?? 1;
        const ga = gaPricer(money);
        const price = ga.price(variant.price, variant.priceArs);
        track("add_to_cart", { currency: ga.currency, value: price * q, items: [{ item_id: variant.sku, item_name: product.name.es, price, quantity: q, item_category: product.category }] });
      },
      setQuantity: (variantId, quantity) => dispatch({ type: "setQuantity", variantId, quantity }),
      remove: (variantId) => dispatch({ type: "remove", variantId }),
      clear: () => dispatch({ type: "clear" }),
      replace: (next) => dispatch({ type: "replace", state: next }),
    }),
    [state, isOpen, money],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
